import {
  DayOfWeek,
  EntityTypes,
  LicensePlate,
  Location,
  PaymentMethodDetails,
  User,
  VRGeneration,
  Vehicle,
  Violation,
} from 'types';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { logError } from './index';
// ------------------------------------------------------------
// DATETIME STRING FORMATTERS
// ------------------------------------------------------------

export const formatDateString = (date: Date | null): string | null => {
  if (!date) return null;
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatTimeString = (date: Date | null): string | null => {
  if (!date) return null;
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const formatDateTimeString = (
  date: Date | string | null | undefined,
): string | null => {
  if (!date) return null;
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return `${formatDateString(date)} ${formatTimeString(date)}`;
};

export const formatShortDateTime = (
  date: Date | null | undefined,
): string | null => {
  if (!date) return null;
  return `${formatDate(date)} ${formatTimeString(date)}`;
};

export const formatUnixToYYYYmmDD = (timestamp: string): string => {
  if (timestamp) {
    const date = new Date(+timestamp * 1000);
    return `${date.getFullYear()}-${
      (date.getMonth() + 1 < 10 ? '0' : '') + (date.getMonth() + 1)
    }-${(date.getDate() < 10 ? '0' : '') + date.getDate()}`;
  } else {
    return '';
  }
};

export const formatDate = (date: Date): string => {
  if (!date || date.getFullYear() === 0 || isNaN(date.getTime())) {
    return '';
  }
  return `${(1 + date.getMonth()).toString().padStart(2, '0')}/${date
    .getDate()
    .toString()
    .padStart(2, '0')}/${date.getFullYear()}`;
};

export const getDayAbbreviation = (day: DayOfWeek): string => {
  const { t } = useTranslation();
  return t(`${day.toLowerCase()}_shortest`);
  // switch (day) {
  //   case DayOfWeek.MONDAY:
  //     return t('mo');
  //   case DayOfWeek.TUESDAY:
  //     return 'Tue';
  //   case DayOfWeek.WEDNESDAY:
  //     return 'Wed';
  //   case DayOfWeek.THURSDAY:
  //     return 'Thu';
  //   case DayOfWeek.FRIDAY:
  //     return 'Fri';
  //   case DayOfWeek.SATURDAY:
  //     return 'Sat';
  //   case DayOfWeek.SUNDAY:
  //     return 'Sun';
  //   default:
  //     return '';
  // }
};

export const formatDateWithUnknownFormat = (value: string): string | null => {
  let oldFormat = '';
  const commonDateFormats = [
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY-MM-DD',
    'MM-DD-YYYY',
    'DD-MM-YYYY',
    'YYYY/MM/DD',
    'MM.DD.YYYY',
    'DD.MM.YYYY',
    'MMM DD, YYYY',
    'DD MMM YYYY',
    'YYYY-MM-DDTHH:mm:ss.SSSZ', // ISO 8601
    'X', // Unix timestamp (seconds)
    'x', // Unix timestamp (milliseconds)
    'YYYY-MM-DD HH:mm:ss', // MySQL DATETIME
    'YYYY-MM-DD HH:mm:ss.SSSSSS', // PostgreSQL TIMESTAMP
    'DD-MMM-YY', // Oracle DATE
    'YYYY-MM-DD HH:mm:ss.SSS', // SQL Server DATETIME
    'MM/DD/YY',
    'DD/MM/YY',
    'YY-MM-DD',
    'MM-DD-YY',
    'DD-MM-YY',
    'YY/MM/DD',
    'MM.DD.YY',
    'DD.MM.YY',
    'DD MMM YY',
    'YY-MM-DD HH:mm:ss', // Short year MySQL DATETIME
  ];
  for (const format of commonDateFormats) {
    if (dayjs(value, format, true).isValid()) {
      oldFormat = format;
    }
  }

  if (oldFormat === '') {
    // maybe throw an error here
    logError(new Error(`Unknown date format: ${value}`));
    return null;
  }

  try {
    const parsedDate = dayjs(value, oldFormat);
    if (parsedDate.isValid()) {
      return parsedDate.format('MM/DD/YYYY');
    } else {
      logError(new Error(`Failed to parse date: ${value}`));
      return null;
    }
  } catch (error) {
    logError(error as Error);
    return null;
  }
};

// ------------------------------------------------------------
// CONTACT/LOCATION STRING FORMATTERS
// ------------------------------------------------------------

export const getFormattedAddress = (l: Location | undefined): string => {
  if (!l) return '';
  return `${l.street || ''} ${l.unit ? '#' + l.unit : ''} ${l.landmark || ''} ${
    l.city || ''
  } ${l.stateCode || ''} ${l.postalCode || ''} ${l.country || ''}`.trim();
};

export const formatAddressLine1 = (l: Location | undefined): string => {
  if (!l) return '';
  return `${l.street || ''} ${l.unit ? '#' + l.unit : ''} ${
    l.landmark || ''
  }`.trim();
};

export const formatAddressLine2 = (l: Location | undefined): string => {
  if (!l) return '';
  return `${l.city || ''} ${l.stateCode || ''} ${l.postalCode || ''}`.trim();
};

export const getShortFormattedAddress = (l: Location): string => {
  return l
    ? `${l.street || ''} ${l.unit ? '#' + l.unit : ''} ${
        l.city ? l.city + ',' : ''
      } ${l.stateCode || ''}`.trim()
    : '';
};

export const formatPhoneNumber = (input: string | undefined): string => {
  if (!input) return '';

  // Check if there's an extension delimiter
  const hasExtension = input.includes('x');
  let mainNumber = input;
  let extension = '';

  if (hasExtension) {
    const parts = input.split('x');
    mainNumber = parts[0];
    extension = parts[1] || '';
  }

  // Remove leading 1 if present
  if (mainNumber.charAt(0) === '1') {
    mainNumber = mainNumber.substring(1);
  }

  const x = mainNumber.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  if (x) {
    const formattedNumber = !x[2]
      ? x[1]
      : `(${x[1]}) ${x[2]}${x[3] ? '-' + x[3] : ''}`;
    return hasExtension && extension
      ? `${formattedNumber} x${extension}`
      : formattedNumber;
  }
  return '';
};

export const parsePhoneNumber = (
  input: string | undefined,
): { mainNumber: string; extension: string } => {
  if (!input) return { mainNumber: '', extension: '' };

  const hasExtension = input.includes('x');
  if (hasExtension) {
    const parts = input.split('x');
    return {
      mainNumber: parts[0].replace(/\D/g, ''),
      extension: parts[1] || '',
    };
  }

  return {
    mainNumber: input.replace(/\D/g, ''),
    extension: '',
  };
};

// ------------------------------------------------------------
// STRING STYLE FORMATTERS
// ------------------------------------------------------------

export const capitalize = (str: unknown): string => {
  if (typeof str !== 'string' || !str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatLabel = (str: string | null): string | null => {
  if (!str) return str;
  if (typeof str !== 'string') return JSON.stringify(str);

  const words = str.split('_');
  return words
    .map((word) => capitalize(word.toLowerCase()))
    .join(' ')
    .trim();
};

export const firstToLowerCase = (str: string | undefined): string => {
  if (!str) return '';
  return (str && str[0].toLowerCase() + str.slice(1)) || str;
};

export const capitalizeFirstLetter = (str: string | undefined): string => {
  if (!str) return '';
  return (str && str[0].toUpperCase() + str.slice(1)) || str;
};

export const formatCurrency = (amount: number): string => {
  return (amount / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

// ------------------------------------------------------------
// NAME FORMATTERS
// ------------------------------------------------------------

export const getFormattedUserName = (user: User): string => {
  return `${capitalize(user.firstName)} ${capitalize(user.lastName)}`;
};

export const getInitialsFromName = (first: string, second: string): string => {
  return `${first.charAt(0)}${second.charAt(0)}`;
};

// ------------------------------------------------------------
// ENUM LABEL FORMATTERS
// ------------------------------------------------------------

export const getEntityLabel = (entityID: number, plural: boolean): string => {
  const { t } = useTranslation();
  const count = plural ? 2 : 1;
  switch (entityID) {
    case 1:
      return t('system.label', { count });
    case 2:
      return t('tenant.label', { count });
    case 3:
      return t('user.label', { count });
    case 4:
      return t('property.label', { count });
    case 5:
      return t('property_group.label', { count });
    case 6:
      return t('truck.label', { count });
    case 7:
      return t('job.label', { count });
    case 8:
      return t('permission.label', { count });
    case 9:
      return t('violation.label', { count });
    case 10:
      return t('rule.label', { count });
    case 11:
      return t('ruleset.label', { count });
    case 12:
      return t('contact.label', { count });
    case 13:
      return t('vehicle.label', { count });
    case 14:
      return t('permit.label', { count });
    case 15:
      return t('payment.label', { count });
    default:
      return '';
  }
};

// ------------------------------------------------------------
// ARRAY STRING FORMATTERS
// ------------------------------------------------------------

export const formatViolationsString = (
  violations: Violation[] | null,
): string | null => {
  if (!violations) return null;
  return violations.map((violation) => violation.name).join(', ');
};

// ------------------------------------------------------------
// VEHICLE STRING FORMATTERS
// ------------------------------------------------------------

export const formatVehicleString = (
  vehicle: Partial<Vehicle> | undefined,
): string => {
  if (!vehicle || (!vehicle.make && !vehicle.model)) return '';
  const template = `${vehicle.year || ''} ${vehicle.color || ''} ${
    vehicle.make
  } ${vehicle.model} ${vehicle.trim || ''}`;
  return template.trim().toUpperCase();
};

export const getGenerationString = (
  generation: VRGeneration | null,
): string => {
  if (!generation) return '';
  return `${generation.start}-${generation.end}`;
};

export const formatLicensePlateString = (
  plate: LicensePlate | undefined | null,
): string => {
  if (!plate) return '';
  const exp = plate?.exp ? `exp: ${dayjs(plate.exp).format('MM/YY')}` : '';
  const state = plate?.state ? `(${plate.state})` : '';
  const number = plate?.number ? plate.number : '';

  return `${number} ${state} ${exp}`.trim();
};

interface FormatDateRangeReportFileNameParams {
  startDate: Date;
  endDate: Date;
  entityType: EntityTypes;
  ext: string;
}
export const formatDateRangeReportFileName = ({
  startDate,
  endDate,
  entityType,
  ext,
}: FormatDateRangeReportFileNameParams): string =>
  `from-${dayjs(startDate).format('MM_DD_YYYY')}-to-${dayjs(endDate).format(
    'MM_DD_YYYY',
  )}-${entityType}-report.${ext}`;

export const formatPaymentMethod = (
  details: PaymentMethodDetails | undefined,
): string => {
  if (!details) return '-';
  const brand = details.brand;
  const last4 = details.lastFour;
  return `${capitalize(brand)} ending in *${last4}`;
};
